// /src/pages/NotificationsPage.js
import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useNavbar } from '../context/NavbarContext';
import { 
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  FilterList as FilterListIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

// Styled components
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: 'left',
  color: theme.palette.text.primary,
  borderRadius: 16,
  boxShadow: theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.06)',
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

const NotificationItem = ({ type, title, message, time, read }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <ListItem 
      sx={{ 
        backgroundColor: (theme) => read ? 'transparent' : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.06),
        borderRadius: '8px',
        mb: 1,
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
        },
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 1 : 0,
      }}
    >
      <ListItemIcon sx={{ minWidth: isMobile ? 'auto' : 56 }}>
        {type === 'Warning' ? <WarningIcon sx={{ color: '#FF9800' }} /> :
         type === 'Info' ? <InfoIcon sx={{ color: '#2196F3' }} /> :
         type === 'Success' ? <CheckCircleIcon sx={{ color: '#4CAF50' }} /> :
         <ErrorIcon sx={{ color: '#F44336' }} />}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexWrap: 'wrap'
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                },
                color: (theme) => theme.palette.text.primary
              }}
            >
              {title}
            </Typography>
            <Chip 
              label={type} 
              size={isMobile ? "small" : "medium"}
              color={type === 'Warning' ? 'warning' : 
                     type === 'Info' ? 'info' : 
                     type === 'Success' ? 'success' : 'error'}
            />
          </Box>
        }
        secondary={
          <Box>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                  md: '0.875rem'
                }
              }}
            >
              {message}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{
                fontSize: {
                  xs: '0.7rem',
                  sm: '0.75rem',
                  md: '0.75rem'
                }
              }}
            >
              {time}
            </Typography>
          </Box>
        }
      />
      <ListItemSecondaryAction sx={{ right: isMobile ? 'auto' : 16, top: isMobile ? 'auto' : '50%' }}>
        <IconButton edge="end" size={isMobile ? "small" : "medium"} sx={{ '&:hover': { backgroundColor: 'rgba(211,47,47,0.10)' }, color: '#D32F2F' }}>
          <DeleteIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const NotificationsPage = () => {
  const { isNavbarOpen } = useNavbar();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [, setSelectedNotification] = React.useState(null);
  const [openDialog, setOpenDialog] = React.useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (notification = null) => {
    setSelectedNotification(notification);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedNotification(null);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: (theme) => theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: {
            xs: '12px',
            sm: '16px',
            md: '16px'
          },
          margin: 0,
          padding: 0,
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant="h4" 
              fontWeight="600"
              sx={{ 
                color: (theme) => theme.palette.text.primary,
                letterSpacing: '-0.5px',
                position: 'relative',
                marginTop: 0,
                fontSize: {
                  xs: '1.5rem',
                  sm: '1.75rem',
                  md: '2rem',
                  lg: '2.25rem',
                  xl: '2.5rem'
                },
              }}
            >
              Notifications
            </Typography>
            <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, mt: { xs: 0.5, sm: 0 } }}>
              Review recent alerts and messages
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
              '& .MuiButton-root': {
                flex: { xs: 1, sm: 'auto' },
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                  md: '0.875rem'
                }
              }
            }}>
              <Button
                startIcon={<FilterListIcon />}
                variant="outlined"
                onClick={handleMenuClick}
                aria-label="Open filter menu"
              >
                Filter
              </Button>
              <Button
                startIcon={<SettingsIcon />}
                variant="outlined"
                onClick={() => handleOpenDialog()}
                aria-label="Open notification settings"
              >
                Settings
              </Button>
            </Box>
          </Box>

          {/* Notification Stats */}
          <Grid container spacing={{ xs: 1, sm: 2, md: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ color: '#FF9800' }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                        md: '1.25rem'
                      }
                    }}
                  >
                    Warnings
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: '#FF9800', 
                    fontWeight: 'bold',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '2rem',
                      md: '2.5rem'
                    }
                  }}
                >
                  5
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.875rem',
                      md: '0.875rem'
                    }
                  }}
                >
                  Requires attention
                </Typography>
              </Item>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon sx={{ color: '#2196F3' }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                        md: '1.25rem'
                      }
                    }}
                  >
                    Info
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: '#2196F3', 
                    fontWeight: 'bold',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '2rem',
                      md: '2.5rem'
                    }
                  }}
                >
                  12
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.875rem',
                      md: '0.875rem'
                    }
                  }}
                >
                  For information
                </Typography>
              </Item>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                        md: '1.25rem'
                      }
                    }}
                  >
                    Success
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: '#4CAF50', 
                    fontWeight: 'bold',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '2rem',
                      md: '2.5rem'
                    }
                  }}
                >
                  8
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.875rem',
                      md: '0.875rem'
                    }
                  }}
                >
                  Completed tasks
                </Typography>
              </Item>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon sx={{ color: '#F44336' }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                        md: '1.25rem'
                      }
                    }}
                  >
                    Errors
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: '#F44336', 
                    fontWeight: 'bold',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '2rem',
                      md: '2.5rem'
                    }
                  }}
                >
                  2
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.875rem',
                      md: '0.875rem'
                    }
                  }}
                >
                  Critical issues
                </Typography>
              </Item>
            </Grid>
          </Grid>

          {/* Notifications List */}
          <Item>
            <List>
              <NotificationItem
                type="Warning"
                title="High Turbidity Detected"
                message="Turbidity levels exceeded threshold in inlet water"
                time="5 minutes ago"
                read={false}
              />
              <Divider />
              <NotificationItem
                type="Info"
                title="Maintenance Scheduled"
                message="System maintenance scheduled for tomorrow at 2 AM"
                time="1 hour ago"
                read={true}
              />
              <Divider />
              <NotificationItem
                type="Success"
                title="Backup Completed"
                message="System backup completed successfully"
                time="2 hours ago"
                read={true}
              />
              <Divider />
              <NotificationItem
                type="Error"
                title="Pump Failure"
                message="Primary pump system failure detected"
                time="3 hours ago"
                read={false}
              />
            </List>
          </Item>
        </Box>

        {/* Filter Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: {
                xs: '200px',
                sm: '220px'
              }
            }
          }}
        >
          <MenuItem 
            onClick={handleMenuClose}
            sx={{
              fontSize: {
                xs: '0.875rem',
                sm: '1rem'
              }
            }}
          >
            All Notifications
          </MenuItem>
          <MenuItem 
            onClick={handleMenuClose}
            sx={{
              fontSize: {
                xs: '0.875rem',
                sm: '1rem'
              }
            }}
          >
            Unread Only
          </MenuItem>
          <MenuItem 
            onClick={handleMenuClose}
            sx={{
              fontSize: {
                xs: '0.875rem',
                sm: '1rem'
              }
            }}
          >
            Warnings & Errors
          </MenuItem>
          <MenuItem 
            onClick={handleMenuClose}
            sx={{
              fontSize: {
                xs: '0.875rem',
                sm: '1rem'
              }
            }}
          >
            Success Messages
          </MenuItem>
        </Menu>

        {/* Settings Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              m: { xs: 2, sm: 4 },
              width: {
                xs: '90%',
                sm: '500px',
                md: '600px'
              }
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: {
              xs: '1.25rem',
              sm: '1.5rem',
              md: '1.75rem'
            }
          }}>
            Notification Settings
          </DialogTitle>
          <DialogContent>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              mt: 2 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 1,
                  fontSize: {
                    xs: '1rem',
                    sm: '1.25rem',
                    md: '1.25rem'
                  }
                }}
              >
                Notification Channels
              </Typography>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={
                  <Typography sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }}>
                    Email Notifications
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={
                  <Typography sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }}>
                    SMS Alerts
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={
                  <Typography sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }}>
                    Push Notifications
                  </Typography>
                }
              />
              <Divider sx={{ my: 2 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 1,
                  fontSize: {
                    xs: '1rem',
                    sm: '1.25rem',
                    md: '1.25rem'
                  }
                }}
              >
                Notification Types
              </Typography>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={
                  <Typography sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }}>
                    System Alerts
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={
                  <Typography sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }}>
                    Maintenance Updates
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={
                  <Typography sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }}>
                    Performance Reports
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={
                  <Typography sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }}>
                    Security Alerts
                  </Typography>
                }
              />
              <Divider sx={{ my: 2 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 1,
                  fontSize: {
                    xs: '1rem',
                    sm: '1.25rem',
                    md: '1.25rem'
                  }
                }}
              >
                Notification Frequency
              </Typography>
              <FormControl fullWidth>
                <InputLabel sx={{
                  fontSize: {
                    xs: '0.875rem',
                    sm: '1rem',
                    md: '1rem'
                  }
                }}>
                  Update Frequency
                </InputLabel>
                <Select 
                  defaultValue="realtime" 
                  label="Update Frequency"
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                        md: '1rem'
                      }
                    }
                  }}
                >
                  <MenuItem value="realtime">Real-time</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCloseDialog}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
              Save Settings
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default NotificationsPage;
